import { cx } from '@twind/core';
import escapeRegExp from 'lodash/escapeRegExp';
import { useState, type ElementType } from 'react';
import ExternalLinkIcon from 'app/components/icons/ExternalLinkIcon';
import useWebchatVersion from 'app/hooks/useWebchatVersion';
import { ChatVersion } from 'app/copied/constants/common';
import useCustomization from 'app/hooks/useCustomization';

import websiteIcon from '../../../assets/icons/domain.png';
import DownloadIcon from '../../../components/icons/DownloadIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import { Copy, Subheading } from '../../../components/Typography';
import VideoPlayer from '../../../components/VideoPlayer';
import { SuggestedEntry } from '../../../copied/models/ChatModules';
import { logError } from '../../../services/logger';
import { checkIsExternalLinkWithImage, getImageItem, getVideoItem } from '../../../services/search';
import TailorNoResults from './TailorNoResults';
import TailorPDFTag from './TailorPDFTag';
import { checkIsPdf } from '../hooks/useLinks';
import usePdfTagsEnabled from '../hooks/usePdfTagsEnabled';

export const highlightText = (text: string, highlightTerms?: string) => {
  try {
    if (!highlightTerms) {
      return text;
    }
    const parts = text.split(new RegExp(`(${escapeRegExp(highlightTerms)})`, 'gi'));

    return parts
      .map((part) => {
        if (part.toLowerCase() === highlightTerms.toLowerCase()) {
          return `<span class="text-highlight">${part}</span>`;
        } else {
          return part;
        }
      })
      .join('');
  } catch (e) {
    logError(e);
    return text;
  }
};

export const SearchResultIcon = ({
  result,
  secondColumnClass,
}: {
  result: SuggestedEntry;
  secondColumnClass: string;
}) => {
  const webChatVersion = useWebchatVersion();
  const customization = useCustomization();

  const isExternalLink = result.resource_link || result.external_link;
  const isWebSearch = result.is_web_search;

  if (webChatVersion === ChatVersion.v4_1 || customization?.fe_config?.v40UnityOverrides?.useUnitySearchResultIcon) {
    return (
      <div className={`${secondColumnClass} flex justify-center`}>
        {isExternalLink ? (
          <ExternalLinkIcon className="h-4 w-4 text-typography-heading" />
        ) : isWebSearch ? (
          <img
            src={websiteIcon}
            alt="Website Icon"
            className="h-6 w-6"
          />
        ) : (
          <RightArrowIcon className="h-6 w-6 text-typography-heading" />
        )}
      </div>
    );
  }

  return (
    <div className={`${secondColumnClass} flex justify-center`}>
      {result.resource_link ? (
        <DownloadIcon className="h-6 w-6 text-typography-heading" />
      ) : isWebSearch ? (
        <img
          src={websiteIcon}
          alt="Website Icon"
          className="h-6 w-6"
        />
      ) : (
        <RightArrowIcon className="h-6 w-6 text-typography-heading" />
      )}
    </div>
  );
};

export const DescriptionV4_1 = ({
  description,
  highlightTerms,
  highlight_tag_text,
}: {
  description: string;
  highlightTerms?: string;
  highlight_tag_text: string;
}) => {
  return (
    <Copy className="search-result-description text-typography-copy">
      {highlight_tag_text && <span className="text-highlight whitespace-pre">{highlight_tag_text}</span>}
      <span
        className="[&>p:last-child]:(inline) line-clamp-2"
        dangerouslySetInnerHTML={{ __html: highlightText(description, highlightTerms) }}
      />
    </Copy>
  );
};

export const DescriptionV4_0 = ({
  description,
  highlightTerms,
  highlight_tag_text,
}: {
  description: string;
  highlightTerms?: string;
  highlight_tag_text: string;
}) => {
  return (
    <Copy className="search-result-description text-typography-copy">
      {highlight_tag_text && <span className="text-highlight whitespace-pre">{highlight_tag_text}</span>}
      <span
        className="[&>p:last-child]:(inline)"
        dangerouslySetInnerHTML={{ __html: highlightText(description, highlightTerms) }}
      />
      <span className="ostro-ellipses">...</span>
    </Copy>
  );
};

export function TailorSearchResult({
  result,
  className,
  onClick,
  highlightTerms,
  showDescription = true,
  articleTitleComponent,
}: {
  result: SuggestedEntry;
  className?: string;
  onClick?: (result: SuggestedEntry) => void;
  highlightTerms?: string;
  showDescription?: boolean;
  articleTitleComponent?: ElementType;
}) {
  const { title } = result;
  const description = result.description || '';
  const highlight_tag_text = result.highlight_tag_text || '';
  const videoItem = getVideoItem(result);
  const imageItem = getImageItem(result);
  const externalLinkWithImage = checkIsExternalLinkWithImage(result);
  const firstColumnClass = videoItem || externalLinkWithImage ? 'col-span-4' : 'col-span-7';
  const secondColumnClass = videoItem || externalLinkWithImage ? 'col-span-4' : 'col-span-1';
  const isDescriptionVisible = description && showDescription;
  const [playing, setPlaying] = useState<string | undefined>();
  const isPDF = checkIsPdf(result.resource_link || result.external_link || '');
  const pdfTagEnabled = usePdfTagsEnabled();
  const displayPDFTag = isPDF && pdfTagEnabled;
  const webchatVersion = useWebchatVersion();

  const TitleComponent = articleTitleComponent || Subheading;

  const DescriptionComponent = webchatVersion === ChatVersion.v4_1 ? DescriptionV4_1 : DescriptionV4_0;

  return (
    <button
      onClick={() => onClick?.(result)}
      className={cx(
        'ostro-tailor-search-result',
        '~(border-typography-divider border-b border-b-[0.4px] py-4 grid grid-cols-8 flex items-center last:border-none text-left block w-full)',
        className
      )}
    >
      <div className={firstColumnClass}>
        <TitleComponent
          className={cx('search-result-title text-typography-heading', isDescriptionVisible ? 'mb-1' : '')}
        >
          <span
            className="![&_:not(sub):not(sup)]:(text-sm) [&_p]:(inline)"
            dangerouslySetInnerHTML={{ __html: highlightText(title, highlightTerms) }}
          />
          {displayPDFTag && <TailorPDFTag />}
        </TitleComponent>
        {isDescriptionVisible && (
          <DescriptionComponent
            description={description}
            highlightTerms={highlightTerms}
            highlight_tag_text={highlight_tag_text}
          />
        )}
      </div>
      {videoItem && (
        <div className={secondColumnClass}>
          <div className="rounded-xl overflow-hidden relative ml-3">
            <VideoPlayer
              playing={playing === videoItem.video_url}
              title={title}
              video_name={videoItem.video_name}
              url={videoItem.video_url}
              height="auto"
              width="100%"
              controls
              muted
              light={videoItem.placeholder_url}
              onClickPreview={(e) => {
                setPlaying(videoItem.video_url);
                e.stopPropagation();
              }}
            />
          </div>
        </div>
      )}
      {externalLinkWithImage && (
        <div className={cx(secondColumnClass, 'ml-3')}>
          <img
            src={imageItem?.url}
            alt={`${title} Image`}
            className="rounded-xl"
          />
        </div>
      )}
      {!videoItem && !externalLinkWithImage && (
        <SearchResultIcon
          result={result}
          secondColumnClass={secondColumnClass}
        />
      )}
    </button>
  );
}

export default function TailorSearchResults({
  results = [],
  searchResultClassName,
  onSelectResult,
  highlightTerms,
  className,
  showArticleDescriptions = true,
  articleTitleComponent,
  showNoResults = false,
}: {
  results?: SuggestedEntry[];
  searchResultClassName?: string;
  onSelectResult?: (result: SuggestedEntry, index: number) => void;
  highlightTerms?: string;
  className?: string;
  showArticleDescriptions?: boolean;
  articleTitleComponent?: ElementType;
  showNoResults?: boolean;
}) {
  return (
    <div
      id="ostro-tailor-search-results-container"
      className={className}
      role="list"
    >
      {showNoResults && results.length === 0 ? (
        <TailorNoResults query={highlightTerms} />
      ) : (
        results.map((result, index) => (
          <TailorSearchResult
            articleTitleComponent={articleTitleComponent}
            showDescription={showArticleDescriptions}
            highlightTerms={highlightTerms}
            className={searchResultClassName}
            key={result.id}
            result={result}
            onClick={() => onSelectResult?.(result, index)}
          />
        ))
      )}
    </div>
  );
}
